import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import imageCropOptions from '../../../../constants/imageCropOptions';
import { isZoomEnabled } from '../../utils/ProductUtils';
import { ClassicCarousel, FeaturedCarousel, OneColumn } from '../../lazy';
import MobileAssets from './MobileAssets';
import ResizeWrapper from '../../shared/ResizeWrapper';
import { mapContextToProps } from '../../OlsShopContext';

export const CLASSIC_CAROUSEL = 'classic';
export const FEATURED_CAROUSEL = 'featured';
export const ONE_COLUMN = 'oneColumn';

const defaultLayout = CLASSIC_CAROUSEL;
const layouts = {
  [CLASSIC_CAROUSEL]: ClassicCarousel,
  [FEATURED_CAROUSEL]: FeaturedCarousel,
  [ONE_COLUMN]: OneColumn
};

const getStyles = ({ mobileContainerPadding }) => ({
  container: {
    'paddingBottom': 'medium',
    '@xs-only': {
      paddingBottom: mobileContainerPadding
    }
  },
  desktopAssets: {
    '@xs-only': {
      display: 'none'
    }
  },
  mobileAssets: {
    'position': 'relative',
    '@sm': {
      display: 'none'
    }
  }
});

export class ProductDetailAssets extends Component {
  constructor(props) {
    super(props);
    this.forceUpdate = this.forceUpdate.bind(this);
  }

  get assets() {
    const { product } = this.props;
    const productAssets = product.assets || product.images;
    if (productAssets && productAssets.length > 0) return productAssets;
    const noProductImage = OlsConfigStore.getState().noProductImage;
    return [
      {
        small_url: noProductImage,
        large_url: noProductImage,
        attachment_height: 600,
        attachment_width: 600
      }
    ];
  }

  get selectedAsset() {
    const { currentAssetIndex } = this.props;
    const asset = this.assets[currentAssetIndex];

    const {
      large_url,
      attachment_file_name,
      url,
      zoom_image_url,
      attachment_width,
      attachment_height,
      attachment_content_type,
      metadata
    } = asset;

    let currentAssetUrl = large_url || attachment_file_name || url;
    let zoomAssetUrl = zoom_image_url || attachment_file_name || url || currentAssetUrl;
    const attachmentWidth = attachment_width;
    const attachmentHeight = attachment_height;
    let type = asset.type || attachment_content_type;

    if (typeof metadata === 'string' || type === 'VIDEO') {
      const parsedMetaData = JSON.parse(metadata);
      if (parsedMetaData?.formatted_url) {
        currentAssetUrl = parsedMetaData.formatted_url;
        type = 'video';
      } else {
        const noProductImage = OlsConfigStore.getState().noProductImage;
        currentAssetUrl = noProductImage;
      }
      zoomAssetUrl = currentAssetUrl;
    }

    return {
      currentAssetUrl,
      zoomAssetUrl: zoomAssetUrl || currentAssetUrl,
      attachmentWidth,
      attachmentHeight,
      type
    };
  }

  get enableOlsImageZoom() {
    const { enableOlsImageZoom = false } = this.props.featureFlags;
    return enableOlsImageZoom && this.props.useZoom;
  }

  renderCarousel({ layout, ...props }) {
    const ProductDetailsAssetsLayout = layouts[layout] || layouts[defaultLayout];
    return <ProductDetailsAssetsLayout { ...props } />;
  }

  get layoutProps() {
    const {
      layout,
      pdpImageCropMethod,
      currentAssetIndex,
      assetsVisible,
      fetching,
      onClickThumbnail,
      pdpImageShape,
      pdpLayout,
      translate,
      isQuickView
    } = this.props;
    const finalizedAssets = this.assets;
    const zoomEnabled =
      this.enableOlsImageZoom && isZoomEnabled(finalizedAssets[currentAssetIndex]);
    const hasFinalizedAssets = !fetching && finalizedAssets.length > 1;

    return {
      assets: this.assets,
      hasFinalizedAssets,
      currentAssetIndex,
      isCurrentAsset: index => currentAssetIndex === index,
      layout,
      onClickThumbnail,
      pdpImageCropMethod,
      pdpImageShape,
      product: this.props.product,
      selectedAsset: this.selectedAsset,
      translate,
      zoomEnabled,
      pdpLayout,
      assetsVisible,
      isQuickView
    };
  }

  render() {
    const { Block } = UX2.Element;
    const { layout, currentAssetIndex } = this.props;

    if (currentAssetIndex && currentAssetIndex >= this.assets.length) {
      return null;
    }

    const mobileContainerPadding = layout === CLASSIC_CAROUSEL ? '0' : 'medium';
    const styles = getStyles({ mobileContainerPadding });

    return (
      <ResizeWrapper onResize={ this.forceUpdate }>
        <Block style={ styles.container }>
          <Block style={ styles.desktopAssets }>{ this.renderCarousel(this.layoutProps) }</Block>
          <Block style={{ ...styles.mobileAssets }}>
            <MobileAssets
              { ...this.props }
              productAssets={ this.assets }
              layoutProps={ this.layoutProps }
              enableMobileZoom={ this.enableOlsImageZoom }
              selectedAsset={ this.selectedAsset }
            />
          </Block>
        </Block>
      </ResizeWrapper>
    );
  }
}

ProductDetailAssets.propTypes = {
  layout: PropTypes.string,
  assetsVisible: PropTypes.number,
  assets: PropTypes.array,
  assetChangedByVariantSelection: PropTypes.any,
  currentAssetIndex: PropTypes.number,
  onClickThumbnail: PropTypes.func,
  renderMode: PropTypes.string,
  fetching: PropTypes.bool,
  product: PropTypes.object,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  pdpLayout: PropTypes.string.isRequired,
  translate: PropTypes.func,
  featureFlags: PropTypes.object,
  isQuickView: PropTypes.bool,
  useZoom: PropTypes.bool
};

ProductDetailAssets.defaultProps = {
  layout: defaultLayout,
  assetsVisible: 5,
  assets: [],
  fetching: false,
  pdpImageCropMethod: imageCropOptions.SHRINK_TO_FIT,
  translate: () => {},
  featureFlags: {},
  useZoom: true
};

export default mapContextToProps(ProductDetailAssets, context => ({
  translate: context.translate,
  featureFlags: context.featureFlags
}));
